'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { getNavItems } from '@/content/nav';
import type { Dictionary } from '@/i18n/dictionary';
import type { Locale } from '@/i18n/config';
import { ButtonLink } from '@/components/ui/button';
import { LogoMark } from '@/components/ui/logo';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from './language-switcher';
import { MobileMenu } from './mobile-menu';

type SiteHeaderProps = {
  dictionary: Dictionary;
  locale: Locale;
};

export function SiteHeader({ dictionary, locale }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const items = getNavItems(dictionary);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        data-scrolled={scrolled || undefined}
        className={cn(
          'fixed inset-x-0 top-0 z-50 animate-fade-in border-b backdrop-blur-md',
          'border-transparent bg-transparent transition-colors duration-350',
          'data-scrolled:border-line data-scrolled:bg-canvas/85',
        )}
        style={{ animationDelay: '0.2s' }}
      >
        <div
          className={cn(
            'container-page flex items-center justify-between gap-6 transition-[height] duration-350',
            scrolled ? 'h-16' : 'h-20',
          )}
        >
          <Link
            href={`/${locale}`}
            aria-label={dictionary.meta.title}
            className="inline-flex min-h-11 items-center gap-3 text-xl font-medium tracking-[-0.02em]"
          >
            <LogoMark />
            <span>Fincrex</span>
          </Link>

          <nav aria-label={dictionary.nav.main} className="hidden gap-0.5 lg:flex">
            {items.map((item) => (
              <Link
                key={item.key}
                href={item.href}
                className="inline-flex min-h-11 items-center rounded-full px-3.5 text-[15px] transition-colors duration-200 hover:bg-ink/6"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <LanguageSwitcher locale={locale} label={dictionary.menu.language} />

            <ButtonLink href="#contact" size="md" className="hidden lg:inline-flex">
              {dictionary.nav.cta}
            </ButtonLink>

            <button
              type="button"
              aria-label={dictionary.menu.open}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
              className="inline-flex size-11 flex-col items-center justify-center gap-[5px] rounded-full border border-line bg-surface/70 lg:hidden"
            >
              <span className="block h-[1.5px] w-4.5 bg-ink" />
              <span className="block h-[1.5px] w-4.5 bg-ink" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <MobileMenu
          dictionary={dictionary}
          locale={locale}
          items={items}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
